import { toast } from 'react-toastify';
import { API } from './Api';
import {
  downloadAuthBlobResponse,
  getBlobErrorMessage,
  parseFilenameFromContentDisposition,
} from './Utils';

/**
 * Secure document / export access — see docs/FE_SECURE_DOCUMENT_ACCESS.md
 * Pattern A: export blobs (JWT + responseType blob)
 * Pattern B: documents/serve/... proxy (JWT + blob open)
 */

const DOCUMENT_SERVE_PREFIX = 'documents/serve/';

/** True when API returned an authenticated document proxy path. */
export const isDocumentServeRef = (value) => {
  if (!value || typeof value !== 'string') return false;
  const trimmed = value.trim().replace(/^\//, '');
  return trimmed.startsWith(DOCUMENT_SERVE_PREFIX);
};

/** Legacy public S3 absolute URL — do not open (bucket is private). */
export const isLegacyS3Url = (value) => {
  if (!value || typeof value !== 'string') return false;
  const href = value.trim().toLowerCase();
  return href.startsWith('http') && href.includes('amazonaws.com');
};

/** Normalize proxy path for axios (relative to API baseURL). */
export const normalizeDocumentRef = (documentRef) => {
  if (!documentRef || typeof documentRef !== 'string') return '';
  return documentRef.trim().replace(/^\//, '');
};

/**
 * GET documents/serve/... with JWT as blob; open in new tab (preview)
 * or force download when options.download === true.
 */
export const openDocument = async (documentRef, options = {}) => {
  const { download = false, fallbackFilename = 'document' } = options;
  const path = normalizeDocumentRef(documentRef);
  if (!path || !isDocumentServeRef(path)) {
    throw new Error('Invalid document reference');
  }

  const url = download ? `${path}${path.includes('?') ? '&' : '?'}download=1` : path;
  const res = await API.get(url, { responseType: 'blob' });
  const type = String(res.headers?.['content-type'] || res.data?.type || '').toLowerCase();

  if (type.includes('application/json') || type.includes('text/json')) {
    const text = await res.data.text();
    let message = 'Document unavailable';
    try {
      const json = JSON.parse(text);
      message = json?.message || message;
    } catch (e) {
      // keep default
    }
    throw new Error(message);
  }

  const blob =
    res.data instanceof Blob
      ? res.data
      : new Blob([res.data], { type: type || 'application/octet-stream' });
  const objectUrl = window.URL.createObjectURL(blob);

  if (download) {
    const filename = parseFilenameFromContentDisposition(
      res.headers?.['content-disposition'],
      fallbackFilename
    );
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
    return true;
  }

  window.open(objectUrl, '_blank', 'noopener,noreferrer');
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 120_000);
  return true;
};

/**
 * Open any document field value from list/detail APIs:
 * - documents/serve/... → authenticated blob open
 * - amazonaws.com → refuse (legacy / broken)
 * - empty → false
 */
export const openSecureDocument = async (value, options = {}) => {
  if (!value || typeof value !== 'string' || !value.trim()) {
    return false;
  }
  const trimmed = value.trim();

  if (isDocumentServeRef(trimmed)) {
    await openDocument(trimmed, options);
    return true;
  }

  if (isLegacyS3Url(trimmed)) {
    throw new Error('Document unavailable');
  }

  if (!/^https?:\/\//i.test(trimmed) && trimmed.includes('documents/')) {
    await openDocument(trimmed, options);
    return true;
  }

  return false;
};

/** Toast-friendly wrapper for UI click handlers. */
export const openSecureDocumentOrToast = async (value, options = {}) => {
  try {
    const ok = await openSecureDocument(value, options);
    if (!ok) {
      toast.error('Document unavailable', {
        position: toast.POSITION.TOP_CENTER,
      });
    }
    return ok;
  } catch (error) {
    toast.error(error?.message || 'Document unavailable', {
      position: toast.POSITION.TOP_CENTER,
    });
    return false;
  }
};

/**
 * Resolve a PPA PDF open target.
 * Prefer documents/serve proxy; if list still returns legacy S3/empty, build proxy from ppa id.
 */
export const resolvePpaDocumentRef = (ppaId, ppaUrl) => {
  if (typeof ppaUrl === 'string' && isDocumentServeRef(ppaUrl)) {
    return normalizeDocumentRef(ppaUrl);
  }
  if (ppaId != null && ppaId !== '') {
    return `documents/serve/ppa/${ppaId}`;
  }
  return '';
};

/**
 * Open PPA PDF via authenticated proxy (after optional download_ppa mark API).
 */
export const openPpaDocumentOrToast = async (ppaId, ppaUrl, options = {}) => {
  const ref = resolvePpaDocumentRef(ppaId, ppaUrl);
  return openSecureDocumentOrToast(ref, {
    download: true,
    fallbackFilename: `ppa_${ppaId || 'download'}.pdf`,
    ...options,
  });
};

/**
 * Resolve Utilization Certificate PDF — prefer proxy ref; else build from row id.
 * Resource: documents/serve/utilization_certificate/{id}
 */
export const resolveUcDocumentRef = (ucId, s3UrlOrRef) => {
  if (typeof s3UrlOrRef === 'string' && isDocumentServeRef(s3UrlOrRef)) {
    return normalizeDocumentRef(s3UrlOrRef);
  }
  if (ucId != null && ucId !== '') {
    return `documents/serve/utilization_certificate/${ucId}`;
  }
  return '';
};

export const openUcDocumentOrToast = async (ucId, s3UrlOrRef, options = {}) => {
  const ref = resolveUcDocumentRef(ucId, s3UrlOrRef);
  return openSecureDocumentOrToast(ref, {
    download: true,
    fallbackFilename: `utilization_certificate_${ucId || 'download'}.pdf`,
    ...options,
  });
};

export const saveExportBlob = downloadAuthBlobResponse;
export const exportBlobErrorMessage = getBlobErrorMessage;
